import dayjs from "dayjs";
import { useEffect, useState } from "react";

import { DEFAULT_RATE_OF_RETURN } from "@constants";
import { BeneficiaryType, GoalType } from "@ctypes";
import { useUser } from "@hooks";
import { calculateAnnualDeposit, calculateInitialBalance } from "@utils";
import { GoalsContext } from "./context";
import { GoalsProviderProps } from "./types";

export const GoalsContextProvider = ({
  beneficiaryId: beneficiaryIdFromProp,
  goalId: goalIdFromProp,
  children,
}: GoalsProviderProps) => {
  const { beneficiaries } = useUser();

  const defaultBeneficiaryId = beneficiaries?.[0]?.id;

  const [beneficiaryId, setBeneficiaryId] = useState(beneficiaryIdFromProp ?? defaultBeneficiaryId);

  // The user query may resolve after this provider mounts (e.g. a direct
  // navigation before the cache is warm). Adopt the id once data arrives
  // instead of staying undefined forever; never clobber an explicit
  // selection.
  /* eslint-disable react-hooks/set-state-in-effect -- sync prop/query data to local state */
  useEffect(() => {
    if (beneficiaryIdFromProp) {
      setBeneficiaryId(beneficiaryIdFromProp);
      return;
    }

    if (!beneficiaryId && defaultBeneficiaryId) {
      setBeneficiaryId(defaultBeneficiaryId);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [beneficiaryIdFromProp, defaultBeneficiaryId]);

  const beneficiary = beneficiaries?.find((b) => b?.id === beneficiaryId);

  const defaultGoalId = beneficiary?.goals?.[0]?.id;

  const [goalId, setGoalId] = useState(goalIdFromProp ?? defaultGoalId);

  useEffect(() => {
    if (!goalIdFromProp) {
      const newGoalId = beneficiary?.goals?.[0]?.id;
      setGoalId(newGoalId);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [beneficiaryId]);

  useEffect(() => {
    if (!goalIdFromProp && !goalId && defaultGoalId) {
      setGoalId(defaultGoalId);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [defaultGoalId]);
  /* eslint-enable react-hooks/set-state-in-effect */

  const goal = beneficiary?.goals?.find((g) => g?.id === goalId);
  const goalIndex = beneficiary?.goals?.findIndex((g) => g?.id === goalId) ?? 0;

  const sip = goal?.sips?.[0];
  const sipId = sip?.id;

  const goalType = goal?.type ?? GoalType.GenerationalWealth;
  const goalName = goal?.name ?? "";

  const targetYear = dayjs(goal?.endDate).year();

  const interest = goal?.projectedRateOfReturn ?? DEFAULT_RATE_OF_RETURN;
  const multiplier = interest / 100 + 1;

  const contributeAfter18 = Boolean(goal?.contributionsAfter18);

  const startYear = dayjs(goal?.startDate).year();
  const endYear = dayjs(goal?.endDate).year();

  const goalLengthInYears = endYear - startYear;
  const numberOfYearsActive = dayjs().year() - startYear;

  const beneficiaryType = beneficiary?.type ?? BeneficiaryType.Child;

  const annualDeposit = calculateAnnualDeposit({ goal });
  const initialBalance = calculateInitialBalance({ goal });

  const props = {
    value: {
      beneficiaryId,
      setBeneficiaryId,
      beneficiary,
      goalId,
      setGoalId,
      goal,
      goalIndex,
      sip,
      sipId,
      goalType,
      goalName,
      interest,
      multiplier,
      targetYear,
      contributeAfter18,
      goalLengthInYears,
      beneficiaryType,
      annualDeposit,
      initialBalance,
      numberOfYearsActive,
      startYear,
      endYear,
    },
  };

  return <GoalsContext.Provider {...props}>{children}</GoalsContext.Provider>;
};
